import { defineStore } from 'pinia'
import { computed, ref, watch } from 'vue'
import { createSyncedRows } from '../services/syncedRows'
import { useSettingsStore } from './settings'
import { useCorpusStore, type CorpusNode } from './corpus'
import { writingDayKey } from '../utils/writingDays'

// An automatic record of writing: words added and removed, and minutes spent
// writing, per page per day. Each device logs only what was typed on it, in
// one row per month, so a row only ever has one writer; totals add the
// devices together. Streaks, goals, and analytics are views over this log.

export interface PageDayLog {
  added: number
  removed: number
  /** Minutes in which there was writing activity, revising in Editor Mode included. */
  minutes: number
  /** Words taken out while revising in Editor Mode: kept apart so revision never counts against the day. */
  cut: number
  /** Editor Mode revision passes finished. */
  passes: number
}

export interface MonthLog {
  /** Keyed by writing day, 'YYYY-MM-DD', then by page id. */
  days: Record<string, Record<string, PageDayLog>>
}

export interface DayTotals {
  added: number
  removed: number
  /** Words added minus removed, floored at zero for the day. */
  net: number
  minutes: number
  /** Words cut while revising; not part of net. */
  cut: number
  passes: number
}

const DEVICE_ID_KEY = 'ink-device-id'
const FLUSH_DELAY_MS = 2_000
const emptyLog = (): PageDayLog => ({ added: 0, removed: 0, minutes: 0, cut: 0, passes: 0 })

/** This device's id, shared by every log kept one row per device. */
export function deviceId(): string {
  let id = localStorage.getItem(DEVICE_ID_KEY)
  if (!id) {
    id = crypto.randomUUID()
    localStorage.setItem(DEVICE_ID_KEY, id)
  }
  return id
}

function isCount(value: unknown): value is number {
  return typeof value === 'number' && Number.isFinite(value) && value >= 0
}

function parseMonth(raw: any): MonthLog | null {
  if (!raw || typeof raw.days !== 'object') return null
  const days: MonthLog['days'] = {}
  for (const [day, pages] of Object.entries<any>(raw.days)) {
    if (!/^\d{4}-\d{2}-\d{2}$/.test(day) || !pages || typeof pages !== 'object') continue
    days[day] = {}
    for (const [pageId, log] of Object.entries<any>(pages)) {
      if (isCount(log?.added) && isCount(log?.removed) && isCount(log?.minutes)) {
        days[day][pageId] = {
          added: log.added,
          removed: log.removed,
          minutes: log.minutes,
          cut: isCount(log.cut) ? log.cut : 0,
          passes: isCount(log.passes) ? log.passes : 0,
        }
      }
    }
  }
  return { days }
}

function emptyTotals(): DayTotals {
  return { added: 0, removed: 0, net: 0, minutes: 0, cut: 0, passes: 0 }
}

export const useWritingLogStore = defineStore('writingLog', () => {
  const settings = useSettingsStore()
  const corpus = useCorpusStore()
  const device = deviceId()

  const rows = createSyncedRows<MonthLog>({
    storageKey: 'ink-writing-log',
    remotePrefix: 'writinglog.',
    parse: parseMonth,
    pushDelayMs: 15_000,
  })

  // Writing is buffered briefly so a keystroke never rewrites stored data.
  const pending = ref<Record<string, Record<string, PageDayLog>>>({})
  let flushTimer: ReturnType<typeof setTimeout> | null = null
  let lastMinute = -1

  const currentDayKey = () => writingDayKey(Date.now(), settings.goalDays.dayStartHour)

  /** Today's writing day; advances on its own when the day rolls over. */
  const today = ref(currentDayKey())
  const refreshToday = () => { today.value = currentDayKey() }
  watch(() => settings.goalDays.dayStartHour, refreshToday)
  if (typeof window !== 'undefined') setInterval(refreshToday, 60_000)

  function buffer(pageId: string, now: number, change: (log: PageDayLog) => void) {
    const day = writingDayKey(now, settings.goalDays.dayStartHour)
    const pages = { ...(pending.value[day] ?? {}) }
    const log = { ...(pages[pageId] ?? emptyLog()) }
    change(log)
    pages[pageId] = log
    pending.value = { ...pending.value, [day]: pages }
    if (!flushTimer) flushTimer = setTimeout(flush, FLUSH_DELAY_MS)
  }

  /**
   * Records a change to a page: a word delta (positive for words written,
   * negative for words removed) and a moment of writing activity. Words
   * removed while revising are logged as cut, so a day spent tightening a
   * draft never loses ground on its word target.
   */
  function record(pageId: string, wordDelta: number, now = Date.now(), options: { revising?: boolean } = {}) {
    const minute = Math.floor(now / 60_000)
    buffer(pageId, now, (log) => {
      if (wordDelta > 0) log.added += wordDelta
      else if (options.revising) log.cut -= wordDelta
      else log.removed -= wordDelta
      if (minute !== lastMinute) {
        log.minutes += 1
        lastMinute = minute
      }
    })
  }

  /** A finished revision pass, which can make the day a writing day. */
  function recordPass(pageId: string, now = Date.now()) {
    buffer(pageId, now, (log) => { log.passes += 1 })
  }

  /** Moves buffered writing into this device's monthly rows. */
  function flush() {
    if (flushTimer) clearTimeout(flushTimer)
    flushTimer = null
    const buffered = pending.value
    pending.value = {}

    const byMonth = new Map<string, [string, Record<string, PageDayLog>][]>()
    for (const [day, pages] of Object.entries(buffered)) {
      const month = day.slice(0, 7)
      byMonth.set(month, [...(byMonth.get(month) ?? []), [day, pages]])
    }
    for (const [month, days] of byMonth) {
      const rowId = `${device}.${month}`
      const current = rows.values.value[rowId] ?? { days: {} }
      const next: MonthLog = { days: { ...current.days } }
      for (const [day, pages] of days) {
        const merged = { ...(next.days[day] ?? {}) }
        for (const [pageId, log] of Object.entries(pages)) {
          const prior = merged[pageId] ?? emptyLog()
          merged[pageId] = {
            added: prior.added + log.added,
            removed: prior.removed + log.removed,
            minutes: prior.minutes + log.minutes,
            cut: prior.cut + log.cut,
            passes: prior.passes + log.passes,
          }
        }
        next.days[day] = merged
      }
      rows.write(rowId, next)
    }
  }

  if (typeof document !== 'undefined') {
    document.addEventListener('visibilitychange', () => {
      if (document.visibilityState === 'hidden') flush()
    })
  }

  // ── Reading ───────────────────────────────────────────────────────────────

  /** Notes: pages whose parent is a page. */
  const noteIds = computed(() => {
    const ids = new Set<string>()
    const walk = (nodes: CorpusNode[], underPage: boolean) => {
      for (const node of nodes) {
        if (underPage && node.subtype === 'page') ids.add(node.id)
        if (node.children?.length) walk(node.children, node.subtype === 'page')
      }
    }
    walk(corpus.tree, false)
    return ids
  })

  /**
   * Pages switched out of the word count are left out of every total, and
   * notes too when the writer counts pages only (Settings > Goals & Habits).
   */
  function counts(pageId: string): boolean {
    if (!settings.goalPrefs.countNotes && noteIds.value.has(pageId)) return false
    return corpus.findNode(pageId)?.includeInWordCount !== false
  }

  /** A day's words: added minus removed, floored at zero, or every word added. */
  function netOf(added: number, removed: number): number {
    return settings.goalPrefs.wordsCount === 'added' ? added : Math.max(0, added - removed)
  }

  function addInto(totals: DayTotals, pages: Record<string, PageDayLog> | undefined) {
    for (const [pageId, log] of Object.entries(pages ?? {})) {
      if (!counts(pageId)) continue
      totals.added += log.added
      totals.removed += log.removed
      totals.minutes += log.minutes
      totals.cut += log.cut
      totals.passes += log.passes
    }
  }

  /** Totals per writing day across every device, buffered writing included. */
  const dayTotals = computed(() => {
    const totals: Record<string, DayTotals> = {}
    const into = (day: string) => (totals[day] ??= emptyTotals())
    for (const month of Object.values(rows.values.value)) {
      for (const [day, pages] of Object.entries(month.days)) addInto(into(day), pages)
    }
    for (const [day, pages] of Object.entries(pending.value)) addInto(into(day), pages)
    for (const day of Object.values(totals)) day.net = netOf(day.added, day.removed)
    return totals
  })

  function totalsFor(day: string): DayTotals {
    return dayTotals.value[day] ?? emptyTotals()
  }

  /**
   * A day's totals within a set of pages (null for every page), for writing
   * goals. Pages left out of the word count are left out here too.
   */
  function totalsWithin(day: string, pageIds: Set<string> | null): DayTotals {
    if (!pageIds) return totalsFor(day)
    const totals = emptyTotals()
    const pages = pageLogsByDay.value[day] ?? {}
    for (const [pageId, log] of Object.entries(pages)) {
      if (!pageIds.has(pageId) || !counts(pageId)) continue
      totals.added += log.added
      totals.removed += log.removed
      totals.minutes += log.minutes
      totals.cut += log.cut
      totals.passes += log.passes
    }
    totals.net = netOf(totals.added, totals.removed)
    return totals
  }

  /** Net words written on a day within a set of pages, floored at zero (or words added, by choice). */
  function netForPages(day: string, pageIds: Set<string>): number {
    let added = 0
    let removed = 0
    const add = (pages: Record<string, PageDayLog> | undefined) => {
      for (const [pageId, log] of Object.entries(pages ?? {})) {
        if (!pageIds.has(pageId)) continue
        added += log.added
        removed += log.removed
      }
    }
    for (const month of Object.values(rows.values.value)) add(month.days[day])
    add(pending.value[day])
    return netOf(added, removed)
  }

  /** Words added minus removed per page, across devices, for days in [from, to]. */
  function pageNetBetween(from: string, to: string): Map<string, number> {
    const totals = new Map<string, number>()
    const add = (day: string, pages: Record<string, PageDayLog>) => {
      if (day < from || day > to) return
      for (const [pageId, log] of Object.entries(pages)) {
        if (!counts(pageId)) continue
        totals.set(pageId, (totals.get(pageId) ?? 0) + (settings.goalPrefs.wordsCount === 'added' ? log.added : log.added - log.removed))
      }
    }
    for (const month of Object.values(rows.values.value)) {
      for (const [day, pages] of Object.entries(month.days)) add(day, pages)
    }
    for (const [day, pages] of Object.entries(pending.value)) add(day, pages)
    return totals
  }

  /** The latest writing day on which each page was written in, across devices. */
  const lastWrittenDay = computed(() => {
    const latest = new Map<string, string>()
    const add = (day: string, pages: Record<string, PageDayLog>) => {
      for (const [pageId, log] of Object.entries(pages)) {
        if (!log.added && !log.removed && !log.minutes && !log.cut && !log.passes) continue
        const prior = latest.get(pageId)
        if (!prior || day > prior) latest.set(pageId, day)
      }
    }
    for (const month of Object.values(rows.values.value)) {
      for (const [day, pages] of Object.entries(month.days)) add(day, pages)
    }
    for (const [day, pages] of Object.entries(pending.value)) add(day, pages)
    return latest
  })

  /**
   * Every page's log per writing day, across devices, buffered writing
   * included. Pages left out of the word count are kept here; callers that
   * report totals leave them out (see `counts`).
   */
  const pageLogsByDay = computed(() => {
    const byDay: Record<string, Record<string, PageDayLog>> = {}
    const add = (day: string, pages: Record<string, PageDayLog>) => {
      const into = (byDay[day] ??= {})
      for (const [pageId, log] of Object.entries(pages)) {
        const prior = into[pageId] ?? emptyLog()
        into[pageId] = {
          added: prior.added + log.added,
          removed: prior.removed + log.removed,
          minutes: prior.minutes + log.minutes,
          cut: prior.cut + log.cut,
          passes: prior.passes + log.passes,
        }
      }
    }
    for (const month of Object.values(rows.values.value)) {
      for (const [day, pages] of Object.entries(month.days)) add(day, pages)
    }
    for (const [day, pages] of Object.entries(pending.value)) add(day, pages)
    return byDay
  })

  /** The earliest day with any logged writing, or null. */
  const firstDay = computed(() => {
    const days = Object.keys(dayTotals.value).sort()
    return days[0] ?? null
  })

  async function syncWithRemote(remote: Parameters<typeof rows.syncWithRemote>[0]) {
    flush()
    await rows.syncWithRemote(remote)
  }

  return {
    record,
    recordPass,
    flush,
    today,
    dayTotals,
    totalsFor,
    totalsWithin,
    netForPages,
    pageNetBetween,
    pageLogsByDay,
    counts,
    lastWrittenDay,
    firstDay,
    syncWithRemote,
  }
})
